import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Zap, CreditCard, ShieldCheck, ArrowLeft, Check } from "lucide-react";

function fmtMxn(n) {
  return "$" + Number(n || 0).toLocaleString("es-MX") + " MXN";
}

export default function CheckoutSubscription() {
  const { sessionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { refresh } = useSubscription();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/checkout/subscription/${sessionId}`)}`);
      return;
    }
    // Fetch plans + find the one this session refers to. We keep it simple:
    // the session_id encodes nothing useful client-side, but /plans is public.
    api
      .get("/subscription-plans")
      .then((r) => {
        const stored = sessionStorage.getItem(`dcc_checkout_${sessionId}`);
        if (stored) {
          const slug = stored;
          const p = (r.data.items || []).find((x) => x.slug === slug);
          if (p) setPlan(p);
          else setError("Plan no disponible.");
        } else {
          // fallback: allow both plans and let user pick; but usually we come from Planes.jsx
          if ((r.data.items || []).length > 0) setPlan(r.data.items[0]);
          else setError("Plan no disponible.");
        }
      })
      .catch(() => setError("Sesión de checkout no encontrada."))
      .finally(() => setLoading(false));
  }, [sessionId, user, authLoading, navigate]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post("/subscriptions/confirm-mock", { session_id: sessionId });
      toast.success("Suscripción activada.");
      try {
        sessionStorage.removeItem(`dcc_checkout_${sessionId}`);
      } catch (_) {}
      await refresh();
      navigate("/app/alumno/suscripcion?welcome=1");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = detail?.code;
      if (code === "already_subscribed") {
        toast.info("Ya tienes una suscripción activa.");
        navigate("/app/alumno/suscripcion");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setConfirming(false);
    }
  };

  if (loading || authLoading) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center text-gray-500">
          Cargando checkout…
        </div>
      </PublicLayout>
    );
  }

  if (error || !plan) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-[#0B0B0B]">{error || "Sesión no encontrada"}</h1>
          <Link to="/planes" className="mt-4 inline-block text-[#F26A21] hover:text-[#D9541A]">
            ← Ver planes
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link
          to="/planes"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#0B0B0B] mb-6"
          data-testid="back-to-plans"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a planes
        </Link>

        <div className="p-8 rounded-2xl border border-gray-200 bg-white" data-testid="checkout-card">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-bold uppercase tracking-widest"
            data-testid="checkout-mock-badge"
          >
            <Zap className="w-3 h-3" /> Checkout mock · sin cargo real
          </span>

          <h1 className="mt-6 text-3xl font-bold text-[#0B0B0B] font-display">
            Confirma tu suscripción
          </h1>

          <div className="mt-6 p-5 rounded-xl bg-[#F9FAFB] border border-gray-200">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
              {plan.billing_cycle === "monthly" ? "Plan mensual" : "Plan anual"}
            </p>
            <h2 className="mt-1 text-xl font-bold text-[#0B0B0B]">{plan.name}</h2>
            <p className="mt-1 text-sm text-gray-600">{plan.description}</p>

            <div className="mt-5 flex items-baseline justify-between border-t border-gray-200 pt-4">
              <span className="text-sm text-gray-600">Total hoy</span>
              <span
                className="text-3xl font-extrabold text-[#0B0B0B] tabular-nums"
                data-testid="checkout-total"
              >
                {fmtMxn(plan.price_mxn)}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-gray-500 text-right font-mono">
              {plan.billing_cycle === "monthly"
                ? "Se renueva mensualmente · cancela cuando quieras"
                : "Se renueva anualmente · cancela cuando quieras"}
            </p>
          </div>

          <div className="mt-6 p-4 rounded-lg border border-dashed border-amber-400 bg-amber-50 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-800 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <p className="font-semibold">MODO MOCK</p>
              <p>
                No se realiza cargo a ninguna tarjeta. Al confirmar activaremos tu suscripción de
                inmediato para fines de demo. En producción esto redirige a Stripe Checkout.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={confirm}
            disabled={confirming}
            data-testid="confirm-mock-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-bold text-sm transition-colors disabled:opacity-60"
          >
            {confirming ? (
              "Confirmando…"
            ) : (
              <>
                <CreditCard className="w-4 h-4" /> Confirmar pago (MOCK)
              </>
            )}
          </button>

          <div className="mt-6 grid grid-cols-3 gap-3 text-[11px] text-gray-500">
            <Perk label="Cancela cuando quieras" />
            <Perk label="Sin permanencia" />
            <Perk label="Facturación electrónica" />
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function Perk({ label }) {
  return (
    <div className="flex items-center gap-1">
      <Check className="w-3 h-3 text-emerald-600" /> <span>{label}</span>
    </div>
  );
}
